// Translation source for the whole app. Key names deliberately match the
// `data-i18n` attribute keys used in the original index.html/en.html so that
// content from later phases (services, calculator, blog, etc.) can be pasted
// in under the same keys without renaming anything.
//
// Phase 1 populated nav + footer keys. Phase 2 adds the hero keys below,
// copied verbatim from the real BS/EN dictionary in index.html's inline
// script (~line 2756 BS / ~2955 EN) — not invented copy. 'hero-scroll' is
// the one new key with no real-site equivalent: the scroll-to-explore prompt
// is a new element required by the master prompt §4.3/4.8 for the futuristic
// hero and didn't exist on the old plain-HTML hero.

export type Lang = 'bs' | 'en'

export const translations = {
  bs: {
    'nav-services': 'Usluge & Cijene',
    'nav-portfolio': 'Portfolio',
    'nav-reviews': 'Recenzije',
    'nav-about': 'O meni',
    'nav-faq': 'FAQ',
    'nav-blog': 'Blog',
    'nav-contact': 'Kontakt',
    'nav-cta': 'Zatražite ponudu',
    'mnav-tagline': 'Studio za web dizajn',
    'footer-rights': 'Sva prava zadržana.',
    'footer-privacy': 'Politika privatnosti',
    'hero-badge': 'Premium studio za web dizajn',
    'hero-h1-pre': 'Web stranice koje',
    'hero-h1-highlight': 'rade za vas',
    'hero-h1-post': ', a ne samo da postoje',
    'hero-sub':
      'Moderne, brze stranice koje donose upite i pokazuju vaš biznis onako kako stvarno izgleda. Od prve ideje do lansiranja.',
    'hero-btn-portfolio': 'Pogledajte radove',
    'hero-scroll': 'Skrolajte da istražite',
    'stat-clients': 'Zadovoljnih klijenata',
    'stat-time': 'Prosječno vrijeme izrade prve verzije',
    'stat-price': 'Početna cijena — bez skrivenih troškova',
    'hero-price-badge': 'Početna cijena',

    // Phase 3 — services & calculator. Copy below is the REAL live-site copy,
    // read directly from the actual `translations` JS dictionary in
    // index.html (the one `applyLang()` reads at runtime) rather than the
    // pre-JS static HTML fallback text, which is stale in a few places.
    'services-title': 'Usluge i cijene',
    'services-sub':
      'Web stranice i grafički dizajn — sve na jednom mjestu. Izračunajte cijenu odmah ili odaberite dizajn usluge.',
    'tab-web': 'Web kalkulator',
    'tab-webdesign': 'Web dizajn',
    'tab-grafika': 'Grafički dizajn',
    'calc-pick': 'Odaberite osnovu',
    'addon-gallery-name': 'Foto galerija',
    'addon-gallery-note': 'Lightbox, kategorije, optimizovano za mobitel',
    'addon-contact-name': 'Kontakt forma',
    'addon-contact-note': 'S dostavljanjem emaila i zaštitom od spama',
    'addon-maps-name': 'Google Maps',
    'addon-maps-note': 'Interaktivna mapa s vašom lokacijom',
    'addon-logo-name': 'Dizajn logotipa',
    'addon-logo-note': 'Profesionalni logo uključen uz stranicu',
    'addon-copy-name': 'Copywriting',
    'addon-copy-note': 'Profesionalni tekst napisan umjesto vas',
    'addon-seo-name': 'Napredni SEO',
    'addon-seo-note': 'Strukturirani podaci, sitemap, robots.txt, meta tagovi',
    'addon-lang-name': 'Višejezična podrška',
    'addon-lang-note': 'BA / EN ili bilo koji drugi jezični par',
    'addon-calc-name': 'Kalkulator cijena',
    'addon-calc-note': 'Interaktivni konfigurator kao ovaj',
    'addon-admin-name': 'Admin panel',
    'addon-admin-note': 'Mijenjajte tekst i fotografije bez koda',
    'addon-db-name': 'Baza podataka',
    'addon-db-note': 'Proizvodi, usluge, objave — sve upravljivo',
    'addon-maint-name': 'Plan održavanja',
    'addon-maint-note': 'Ažuriranja, backup, sigurnosne provjere',
    'addon-maint-price': '/ mj',
    'lang-pick': 'Odaberite jezik...',
    'lang-en': 'Engleski',
    'lang-de': 'Njemački',
    'lang-custom': 'Drugi (navedite u poruci)',
    'lang-add-btn': 'Dodaj',
    'calc-config-label': 'Vaša konfiguracija',
    'tier-basic': 'Osnovna stranica',
    'tier-pro': 'Profesionalna stranica',
    'tier-premium': 'Premium stranica',
    'calc-price-label': 'Okvirna cijena',
    'calc-note': '* Konačna cijena se potvrđuje nakon konsultacije. Bez skrivenih troškova.',
    'calc-cta': 'Dodaj ovo u upit →',
    'calc-reset': 'Resetuj',
    'calc-show-more': 'Prikaži još',
    'calc-show-less': 'Prikaži manje',
    'calc-total-label': 'Ukupno',
    'calc-language-total-label': 'Jezici',
    'calc-no-items': 'Odaberite stavke',
    'bundle-title': 'Kombinujete usluge? Cijena automatski pada.',
    'bundle-sub': 'Odaberite Web dizajn + Grafički dizajn i popust se primjenjuje u realnom vremenu.',
    'bundle-discount-label': 'Paketni popust',
    'bundle-th-service': 'Usluga',
    'bundle-th-standalone': 'Samostalno',
    'bundle-th-with': 'S paketom',
    'bundle-th-saved': 'Ušteđeno',
    'bundle-th-pay': 'Plaćate',
    'bundle-total': 'UKUPNO',
    'bundle-empty': 'Odaberite bar jednu stavku iz kalkulatora da vidite paketnu uštedu.',
    'wd-intro-pre': 'Već imate stranicu ali ne donosi rezultate? Dodajte funkcionalnosti po standardnim cijenama. Ako odlučite',
    'wd-intro-link': 'napraviti novu stranicu',
    'wd-intro-post': ', iste stavke koštaju manje — do 20% popusta na većinu dodataka.',
    'wd-gallery-note': 'Lightbox, kategorije, optimizovano za mobitel',
    'wd-contact-note': 'S dostavljanjem emaila i zaštitom od spama',
    'wd-maps-note': 'Interaktivna mapa s vašom lokacijom',
    'wd-copy-note': 'Profesionalni tekst napisan umjesto vas',
    'wd-seo-note': 'Strukturirani podaci, sitemap, robots.txt, meta tagovi',
    'wd-calc-note': 'Interaktivni konfigurator kao ovaj',
    'wd-admin-note': 'Mijenjajte tekst i fotografije bez koda',
    'wd-db-note': 'Proizvodi, usluge, objave — sve upravljivo',
    'wd-total-note': 'Cijene su u EUR i vrijede za dodavanje na postojeću stranicu.',
    'grafika-intro':
      'Grafički dizajn usluge koje možete naručiti samostalno. Kliknite na karticu da je dodate u upit.',
    'grafika-selected': 'Odabrane usluge',
    'grafika-none-selected': 'Nije odabrano ništa',
    'grafika-services': 'usluga',
    'grafika-total': 'Ukupno',
    'grafika-from': 'od',
    'grafika-logo-discount-pre': 'Ako kupiš',
    'grafika-logo-discount-mid': 'logo uz web stranicu',
    'grafika-logo-discount-post': 'kroz kalkulator, cijena pada — pogledaj Web kalkulator.',
    'grafika-combo-note': 'Kombinuj grafiku s izradom web stranice za dodatni popust — pogledaj Web kalkulator.',

    // Phase 4 — Portfolio, Reviews, Process, About, FAQ. Real copy read
    // directly from index.html's #portfolio/#recenzije/#kako-funkcionise/
    // #o-meni/#faq sections and the live `translations` JS dictionary.
    // Project-specific content (names, descriptions, case studies) lives in
    // lib/portfolio.ts / lib/testimonials.ts / lib/process.ts / lib/faq.ts
    // as bs/en pairs instead, matching the existing GRAFIKA_ITEMS pattern —
    // these keys are only the shared section chrome.
    'portfolio-eyebrow': 'Naši radovi',
    'portfolio-title': 'Portfolio',
    'portfolio-sub': 'Svaki projekat je jedinstven. Pogledajte šta smo uradili za naše klijente.',
    'ba-before': 'Prije',
    'ba-after': 'Poslije',
    'pf-case-study': 'Case study',
    'pf-overview': 'Pregled projekta',
    'pf-problem': 'Problem',
    'pf-solution': 'Rješenje',
    'pf-result': 'Rezultat',
    'pf-techstack': 'Tech stack',
    'pf-expand': 'Vidi detalje',
    'pf-collapse': 'Sakrij detalje',
    'pf-case-study-link': 'Pogledaj cijeli case study →',
    'reviews-eyebrow': 'Šta kažu klijenti',
    'reviews-title': 'Recenzije',
    'reviews-sub': 'Ne vjerujte samo nama na riječ — evo šta kažu oni koji su već surađivali s nama.',
    'how-eyebrow': 'Proces rada',
    'how-title': 'Kako funkcioniše',
    'how-sub': 'Četiri koraka od prvog razgovora do vaše nove stranice — bez komplikacija.',
    'about-eyebrow': 'Ko sam ja',
    'about-title': 'O meni',
    'about-p1':
      'Zdravo! Ja sam Ermin Avdibašić, web dizajner iz Kalesije, Kanton Tuzla, i osnivač EA Studio. Bavim se izradom web stranica koje ne samo da izgledaju lijepo, već donose stvarne rezultate — više upita, bolje prvo dojmove, i prisustvo koje vaš biznis zaslužuje. Vjerujem da svaka firma, bez obzira na veličinu ili industriju, treba online prisustvo koje ozbiljno predstavlja njen rad.',
    'about-p2':
      'Svaki projekat pristupam pažljivo i s punom posvećenošću, blisko surađujući s klijentom od prve ideje do lansiranja stranice. Moj cilj je da vaša web stranica bude vizualno atraktivna, brza, i prilagođena ljudima koji vas zovu da naruče posao — ne samo da "izgleda lijepo".',
    'about-meta-desc':
      'Ermin Avdibašić, osnivač EA Studio — web dizajner iz Kalesije koji gradi stranice koje donose stvarne rezultate.',
    'faq-eyebrow': 'Često postavljana pitanja',
    'faq-title': 'Imate pitanja?',
    'faq-sub': 'Evo odgovora na najčešća pitanja koja dobijam od klijenata.',
    'faq-blog-cta-pre': 'Trebate više detalja ili želite naučiti nešto novo?',
    'faq-blog-cta-link': 'Pogledajte blog',
    'faq-blog-cta-post': 'za korisne savjete o web dizajnu, SEO-u i digitalnom prisustvu.',

    // Phase 5 — Blog. Shared UI chrome only; real post content (titles,
    // excerpts, bodies, dates) lives in lib/blog.ts as bs/en pairs, same
    // pattern as Phase 4's project/testimonial/FAQ data.
    'blog-eyebrow': 'Blog',
    'blog-title': 'Savjeti i uvidi',
    'blog-sub': 'Kratki, praktični tekstovi o web dizajnu, SEO-u i digitalnom prisustvu za male firme.',
    'blog-all-posts': 'Svi članci',
    'blog-back': 'Nazad na blog',
    'blog-read-more': 'Pročitaj više',
    'blog-min-read': 'min čitanja',
    'blog-not-found-title': 'Članak nije pronađen',
    'blog-not-found-sub': 'Članak koji tražite ne postoji ili je premješten.',
    'blog-not-found-cta': 'Nazad na blog',
    'blog-breadcrumb-home': 'Početna',

    // Phase 5.5 — Inquiry modal (Formspree). Real copy read directly from
    // index.html's #contactForm translations block (form-fname..form-calc-
    // label keys, ~line 2793-2941), reused here since the calculator CTAs
    // now submit to the same Formspree endpoint instead of just opening
    // WhatsApp. 'inquiry-modal-title'/'inquiry-modal-sub'/'form-error'/
    // 'inquiry-modal-close'/'form-submitting'/'form-email-ph' are new — no
    // real-site equivalent (the live form isn't inside a modal).
    'inquiry-modal-title': 'Pošaljite upit',
    'inquiry-modal-sub': 'Vaš odabir iz kalkulatora je već uključen ispod — ostavite kontakt podatke i pošaljite.',
    'inquiry-modal-close': 'Zatvori',
    'form-calc-label': 'Vaša konfiguracija iz kalkulatora',
    'form-fname': 'Ime',
    'form-fname-ph': 'Vaše ime',
    'form-lname': 'Prezime',
    'form-lname-ph': 'Vaše prezime',
    'form-email': 'Email adresa',
    'form-email-ph': 'vas@email.com',
    'form-msg': 'Poruka (opcionalno)',
    'form-msg-ph': 'Dodatne napomene ili pitanja...',
    'form-submit': 'Pošalji upit →',
    'form-submitting': 'Slanje...',
    'form-success-title': 'Hvala! Upit je primljen.',
    'form-success-sub': 'Javit ću se u roku 24 sata. Ako hitno trebate odgovor, slobodno me kontaktirajte direktno na WhatsApp.',
    'form-success-wa': 'Pišite na WhatsApp',
    'form-error': 'Došlo je do greške pri slanju. Molimo pokušajte ponovo ili nas kontaktirajte direktno putem WhatsApp-a.',

    // Phase 6 — Kontakt page chrome (real routed /kontakt page, replacing
    // the old #kontakt anchor). The 15-question form itself is intentionally
    // BS-only — see lib/questionnaire.ts header comment for why — but this
    // page's heading/intro follows the site's normal i18n pattern.
    'kontakt-eyebrow': 'Započnimo saradnju',
    'kontakt-title': 'Kontakt',
    'kontakt-sub':
      'Pišite direktno putem WhatsApp-a ili Vibera, ili ispunite kratki upitnik ispod kako bih odmah imao/la sve informacije potrebne za izradu vaše web stranice.',
    'kontakt-form-title': 'Ili nam pišite direktno',
    'kontakt-form-sub': 'Kraća opcija — samo ime, email i poruka.',

    // Phase 7 — 404 page (no real-site equivalent, new page).
    '404-eyebrow': 'Greška 404',
    '404-title': 'Ova stranica je odlutala u svemir',
    '404-sub': 'NLO ju je vjerovatno odnio. Provjerite adresu ili se vratite tamo gdje sve ima smisla.',
    '404-cta-home': 'Nazad na početnu',
    '404-cta-contact': 'Kontaktirajte me',

    // Phase 7 — accessibility (skip link) + Privacy Policy page (new,
    // standard/generic policy — no real-site equivalent existed, the old
    // footer's /privacy.html link was dead).
    'skip-to-content': 'Preskoči na sadržaj',
    'privacy-eyebrow': 'Pravne informacije',
    'privacy-title': 'Politika privatnosti',
    'privacy-intro':
      'Ova politika objašnjava koje podatke EA Studio prikuplja putem ove web stranice, kako se koriste i kome se ne prosljeđuju.',
    'privacy-s1-title': '1. Ko prikuplja podatke',
    'privacy-s1-body':
      'Podatke prikuplja Ermin Avdibašić, vlasnik EA Studio, kao fizičko lice koje se bavi izradom web stranica i grafičkim dizajnom u Kalesiji, Kanton Tuzla, BiH. Za sva pitanja o ovoj politici možete me kontaktirati putem podataka navedenih na stranici Kontakt.',
    'privacy-s2-title': '2. Koji podaci se prikupljaju',
    'privacy-s2-body':
      'Kada popunite kalkulator upita, upitnik na stranici Kontakt ili kratku kontakt formu, prikupljaju se podaci koje sami unesete: ime, email adresa i sadržaj poruke, te po potrebi vaš odabir iz kalkulatora usluga. Stranica ne prikuplja podatke automatski osim jezične preferencije (vidi tačku 4).',
    'privacy-s3-title': '3. Kako se podaci koriste',
    'privacy-s3-body':
      'Podaci iz formi šalju se direktno na moj email putem servisa Formspree, isključivo radi odgovaranja na vaš upit i eventualne izrade ponude. Podaci se ne koriste u marketinške svrhe i ne prodaju se niti iznajmljuju trećim licima.',
    'privacy-s4-title': '4. Kolačići, lokalno pohranjivanje i Google Analytics',
    'privacy-s4-body':
      'Stranica čuva odabrani jezik (bosanski/engleski) lokalno u vašem pregledniku putem localStorage-a — ovaj podatak nikada ne napušta vaš uređaj. Stranica koristi i Google Analytics za razumijevanje posjećenosti, ali samo ako to prihvatite u banneru za kolačiće prilikom prve posjete. Google Analytics kolačići se ne postavljaju dok ne kliknete "U redu" — ako kliknete "Odbij", Analytics se uopšte ne učitava. Svoj izbor možete promijeniti brisanjem podataka o web lokaciji za ovaj sajt u podešavanjima pretraživača.',
    'privacy-s5-title': '5. Treće strane',
    'privacy-s5-body':
      'Slanje formi obrađuje Formspree (formspree.io), nezavisan servis za obradu web formi — njihova politika privatnosti primjenjuje se na prijenos podataka putem njihove infrastrukture. Stranica se hostuje putem Netlify-a, koji može bilježiti standardne tehničke podatke servera (npr. IP adresu) u svrhu rada i sigurnosti hostinga.',
    'privacy-s6-title': '6. Sigurnost podataka',
    'privacy-s6-body':
      'Ova stranica nema vlastitu bazu podataka niti server koji čuva vaše podatke — sve forme šalju podatke direktno na Formspree preko šifrovane (HTTPS) veze, bez posrednog skladišta koje bi EA Studio držao ili moglo izgubiti. Sve forme imaju i osnovnu zaštitu od automatizovanog spama.',
    'privacy-s7-title': '7. Vaša prava',
    'privacy-s7-body':
      'U svakom trenutku možete zatražiti uvid, ispravku ili brisanje podataka koje ste mi poslali putem neke od formi na ovoj stranici — dovoljno je da mi to javite putem emaila, WhatsApp-a ili Vibera navedenih na stranici Kontakt.',
    'privacy-updated': 'Posljednje ažurirano: august 2026.',

    // Sections 6-7 were renumbered from the old 5-section layout when a new
    // Security section was inserted — PrivacyPage.tsx's `sections` array
    // now lists 7 entries (s1..s7) instead of 6.

    // Grafički dizajn & brendiranje — dedicated SEO landing page (SiteFooter
    // "Više" column), reuses GRAFIKA_ITEMS from lib/pricing.ts for the
    // actual service list/pricing rather than duplicating it. Real proof
    // pieces (logo/business card/roadside ad assets) come from lib/portfolio.ts.
    'gd-eyebrow': 'Grafički dizajn',
    'gd-title': 'Grafički dizajn i brendiranje',
    'gd-sub':
      'Logo, vizit karte, printani materijali i brend identitet za firme u BiH — od prve skice do gotovog dizajna spremnog za štampu ili web.',
    'gd-intro-title': 'Vizualni identitet koji se pamti',
    'gd-intro-body':
      'Bilo da tek pokrećete firmu ili obnavljate postojeći identitet, dobar dizajn je često prva stvar koju klijent primijeti. Radim logotipe, vizit karte, postere, reklame i sve ostalo što vašem brendu treba da izgleda profesionalno — na ekranu i u štampi.',
    'gd-services-title': 'Usluge grafičkog dizajna',
    'gd-services-sub': 'Svaka usluga se može naručiti samostalno, ili kombinovati s izradom web stranice za dodatni popust.',
    'gd-proof-title': 'Stvarni radovi, ne stock fotografije',
    'gd-proof-sub': 'Logotipi, vizit karte i reklame koje su stvarno u upotrebi kod naših klijenata.',
    'gd-proof-tehnostan-logo': 'Logo za Tehnostan — kamenorezačku firmu iz Kalesije',
    'gd-proof-tehnostan-card': 'Vizit karta za Tehnostan',
    'gd-proof-tehnostan-ad': 'Putna reklama za Tehnostan',
    'gd-proof-mmmetal-logo': 'Logo za MM Metal — metaloprerađivačku firmu',
    'gd-combo-title': 'Kombinujte s web stranicom za popust',
    'gd-combo-body':
      'Ako naručite logo zajedno s izradom nove web stranice, cijena loga pada — pogledajte Web kalkulator za tačan izračun s popustom.',
    'gd-combo-cta': 'Otvorite kalkulator',
    'gd-cta-title': 'Spremni za novi vizualni identitet?',
    'gd-cta-sub': 'Pošaljite upit i recite mi šta vam treba — javljam se u roku 24 sata.',
    'gd-cta-btn': 'Zatražite ponudu',

    // Print materijali — dedicated SEO landing page, second of 4 (SiteFooter
    // "Više" column). Reuses GraphicDesignPanel filtered to print-specific
    // items (filterIds) so it doesn't duplicate the full grid already shown
    // on /graficki-dizajn. Proof images reuse the roadside-ad and business-
    // card assets from lib/portfolio.ts, consistent with page 1's approach.
    'pm-eyebrow': 'Print materijali',
    'pm-title': 'Print materijali i štampani dizajn',
    'pm-sub':
      'Vizit karte, posteri, putne reklame, meniji i ambalaža — dizajn pripremljen za štampu, ne samo za ekran.',
    'pm-intro-title': 'Dizajn koji dobro izgleda i u ruci, ne samo na monitoru',
    'pm-intro-body':
      'Štampani materijal ima drugačija pravila od web dizajna — CMYK boje, rezolucija za štampu, bleed margine. Pripremam sve fajlove tako da budu spremni za štampariju, bez iznenađenja i dodatnih ispravki.',
    'pm-services-title': 'Usluge za štampu',
    'pm-services-sub': 'Svaka usluga se može naručiti samostalno, ili kombinovati s izradom web stranice za dodatni popust.',
    'pm-proof-title': 'Materijali koji su stvarno odštampani',
    'pm-proof-sub': 'Putne reklame i vizit karte koje naši klijenti stvarno koriste na terenu.',
    'pm-proof-tehnostan-ad': 'Putna reklama za Tehnostan — velikoformatna štampa',
    'pm-proof-tehnostan-ad2': 'Druga putna reklama za Tehnostan',
    'pm-proof-tehnostan-card': 'Vizit karta za Tehnostan — obostrani print',
    'pm-combo-title': 'Kombinujte s web stranicom za popust',
    'pm-combo-body':
      'Print materijali se mogu naručiti uz izradu nove web stranice po povoljnijoj cijeni — pogledajte Web kalkulator.',
    'pm-combo-cta': 'Otvorite kalkulator',
    'pm-cta-title': 'Trebate nešto spremno za štampu?',
    'pm-cta-sub': 'Pošaljite upit s detaljima — javljam se u roku 24 sata.',
    'pm-cta-btn': 'Zatražite ponudu',

    // Izrada web stranica po gradovima — dedicated SEO landing page, third
    // of 4. BS version lists coverage across major BiH cities (grid of city
    // names); EN version instead emphasises remote delivery worldwide, per
    // explicit user instruction — English-speaking clients aren't limited
    // to BiH the way BS-speaking search traffic is.
    'wc-eyebrow': 'Izrada web stranica',
    'wc-title': 'Izrada web stranica u vašem gradu',
    'wc-sub':
      'Cijela saradnja se odvija online — poziv, dogovor, izrada i lansiranje — bez obzira gdje se nalazite u Bosni i Hercegovini.',
    'wc-intro-title': 'Grad nije prepreka',
    'wc-intro-body':
      'Radim isključivo online — putem WhatsApp-a, Vibera, video poziva i emaila — što znači da je proces izrade web stranice identičan bez obzira da li ste iz Kalesije, Sarajeva ili bilo kojeg drugog grada u BiH. Nema potrebe za fizičkim sastancima.',
    'wc-cities-title': 'Gradovi u kojima smo do sada radili ili primamo upite',
    'wc-cities-sub': 'Ovo je samo dio gradova — ako vašeg nema na listi, to ne znači da ne radimo tamo.',
    'wc-remote-title': 'Kako izgleda saradnja na daljinu',
    'wc-remote-body':
      'Sav dogovor, slanje materijala i pregled napretka odvija se putem chat aplikacija i emaila. Stranica se hostuje online, pa je pregledavate uživo tokom cijelog procesa izrade — bez obzira odakle pristupate.',
    'wc-combo-title': 'Ista cijena, bez obzira na lokaciju',
    'wc-combo-body': 'Cijene iz kalkulatora važe jednako za sve gradove u BiH — nema doplate za udaljenost.',
    'wc-combo-cta': 'Otvorite kalkulator',
    'wc-cta-title': 'Spremni da počnemo, bez obzira gdje se nalazite?',
    'wc-cta-sub': 'Pošaljite upit i recite mi odakle ste — javljam se u roku 24 sata.',
    'wc-cta-btn': 'Zatražite ponudu',

    // Detaljni cjenovnik — dedicated SEO landing page, fourth and last of 4.
    // A static, fully crawlable price list (unlike the homepage calculator,
    // which only renders selections after JS interaction) — pulls every
    // number directly from lib/pricing.ts (ADDONS/WD_ADDONS/GRAFIKA_ITEMS/
    // BASE_PRICE/thresholds) so nothing can drift out of sync with the real
    // calculator. Targets "cijena izrade web stranice" / "cjenovnik" search
    // intent, where people want to see numbers without clicking around.
    'cj-eyebrow': 'Cjenovnik',
    'cj-title': 'Detaljan cjenovnik usluga',
    'cj-sub':
      'Sve cijene na jednom mjestu — izrada nove web stranice, dodaci za postojeću stranicu, i grafički dizajn. Bez skrivenih troškova.',
    'cj-intro-title': 'Zašto javne cijene',
    'cj-intro-body':
      'Vjerujem da bi cijena trebala biti jasna prije nego što pošaljete upit. Ispod su iste cijene koje koristi i interaktivni kalkulator na početnoj stranici — ovdje su samo prikazane kao tabela, za brzi pregled.',
    'cj-tiers-title': 'Osnovna cijena nove web stranice',
    'cj-tiers-sub': 'Cijena zavisi od broja dodataka koje odaberete — tri praga određuju nivo stranice.',
    'cj-tier-basic-range': 'do {threshold} EUR',
    'cj-tier-pro-range': '{threshold}–{next} EUR',
    'cj-tier-premium-range': '{threshold}+ EUR',
    'cj-tiers-base-note': 'Početna cijena (prazna osnova, bez dodataka): {price} EUR.',
    'cj-addons-title': 'Dodaci za novu web stranicu',
    'cj-addons-sub': 'Cijene s popustom kada se kombinuju s izradom nove stranice. Standardna cijena vrijedi za samostalnu narudžbu.',
    'cj-th-service': 'Usluga',
    'cj-th-standalone': 'Samostalno',
    'cj-th-bundled': 'Uz novu stranicu',
    'cj-wd-title': 'Dodaci za postojeću web stranicu',
    'cj-wd-sub': 'Već imate stranicu? Ovo su fiksne cijene za dodavanje funkcionalnosti na postojeću stranicu.',
    'cj-th-price': 'Cijena',
    'cj-lang-title': 'Višejezična podrška',
    'cj-lang-body': 'Dodatni jezik (npr. engleski ili njemački) uz web stranicu: {price} EUR po jeziku.',
    'cj-grafika-title': 'Grafički dizajn',
    'cj-grafika-sub': 'Cijene za samostalnu narudžbu grafičkog dizajna — pogledajte i stranicu Grafički dizajn za primjere radova.',
    'cj-cta-title': 'Vidite tačno koliko košta vaš projekat',
    'cj-cta-sub': 'Otvorite interaktivni kalkulator za tačan izračun s vašim odabirom, ili pošaljite upit direktno.',
    'cj-cta-calc': 'Otvorite kalkulator',
    'cj-cta-btn': 'Zatražite ponudu',

    // Terms of Service — new /terms page, same section-array pattern as
    // /privacy. Generic freelance-studio terms, drafted — not reviewed by
    // a lawyer, same disclaimer treatment as the Privacy Policy originally
    // got. Written this session alongside the cookie/localStorage banner
    // and a client-data-safety review (see handoff.md).
    'terms-eyebrow': 'Pravne informacije',
    'terms-title': 'Uslovi korištenja',
    'terms-intro':
      'Ovi uslovi opisuju kako EA Studio pruža usluge izrade web stranica, grafičkog dizajna i print materijala. Korištenjem naših usluga ili slanjem upita prihvatate ove uslove.',
    'terms-s1-title': '1. Usluge',
    'terms-s1-body':
      'EA Studio pruža usluge izrade web stranica, grafičkog dizajna, brendiranja i print materijala, za individualne klijente, mala preduzeća i startupe. Tačan obim svakog projekta dogovara se pojedinačno prije početka rada, obično putem upitnika ili direktnog razgovora.',
    'terms-s2-title': '2. Ponude i cijene',
    'terms-s2-body':
      'Cijene prikazane na stranici Cjenovnik i u kalkulatoru su okvirne procjene. Konačna cijena se potvrđuje nakon konsultacije i zavisi od stvarnog obima projekta. Ponuda važi 14 dana od datuma slanja, osim ako nije drugačije naznačeno.',
    'terms-s3-title': '3. Plaćanje',
    'terms-s3-body':
      'Za veće projekte obično se traži avans prije početka rada, s ostatkom plaćivim po završetku ili prema dogovorenoj dinamici. Tačni uslovi plaćanja dogovaraju se pojedinačno i navode se u ponudi ili prepisci prije početka rada.',
    'terms-s4-title': '4. Rokovi izrade',
    'terms-s4-body':
      'Okvirni rok izrade dogovara se prije početka projekta. Rokovi zavise od obima posla i brzine dostavljanja materijala i povratnih informacija od strane klijenta — kašnjenje u dostavljanju sadržaja ili odgovora može produžiti rok izrade.',
    'terms-s5-title': '5. Revizije i izmjene',
    'terms-s5-body':
      'Razuman broj revizija je uključen u cijenu većine usluga, dogovoren pojedinačno po projektu. Zahtjevi za izmjene van dogovorenog obima (npr. potpuno nova verzija dizajna nakon odobrenja) mogu se naplaćivati dodatno.',
    'terms-s6-title': '6. Vlasništvo i autorska prava',
    'terms-s6-body':
      'Do potpune uplate dogovorenog iznosa, svi materijali izrađeni u sklopu projekta (dizajn, kod, grafika) ostaju vlasništvo EA Studio. Nakon potpune uplate, vlasništvo i prava korištenja finalnih materijala prenose se na klijenta, osim ako nije drugačije dogovoreno. EA Studio zadržava pravo prikazivanja završenih projekata u svom portfoliju, osim ako klijent izričito zatraži drugačije.',
    'terms-s7-title': '7. Sadržaj koji dostavlja klijent',
    'terms-s7-body':
      'Klijent je odgovoran za tačnost, zakonitost i vlasništvo nad svim materijalima koje dostavi (tekstovi, fotografije, logotipi, podaci) za korištenje u projektu. EA Studio ne snosi odgovornost za kršenje autorskih prava trećih lica u materijalima koje je dostavio klijent.',
    'terms-s8-title': '8. Ograničenje odgovornosti',
    'terms-s8-body':
      'EA Studio ulaže razuman trud da usluge budu kvalitetne i pouzdane, ali ne garantuje neprekidan rad web stranica nakon lansiranja (npr. usljed problema kod hosting provajdera ili trećih servisa). Odgovornost EA Studio, u mjeri dozvoljenoj zakonom, ograničena je na iznos koji je klijent stvarno platio za dotičnu uslugu.',
    'terms-s9-title': '9. Otkazivanje',
    'terms-s9-body':
      'Klijent može otkazati projekat u bilo kojem trenutku prije završetka, uz obavezu plaćanja za već obavljeni rad do trenutka otkazivanja. Uplaćeni avans se u tom slučaju ne vraća ako je rad na projektu već započet.',
    'terms-s10-title': '10. Mjerodavno pravo',
    'terms-s10-body':
      'Ovi uslovi tumače se u skladu sa zakonima Bosne i Hercegovine. Svaki spor pokušat će se prvo riješiti dogovorom putem direktne komunikacije.',
    'terms-updated': 'Posljednje ažurirano: august 2026.',

    // Cookie/local-storage consent banner — bottom-fixed, mounted globally
    // in App.tsx. Framed around what the site actually does (one
    // functional localStorage key for language, no cookies, no trackers)
    // rather than a generic multi-category cookie picker that wouldn't
    // match reality.
    'cookie-banner-text':
      'Ova stranica koristi Google Analytics za razumijevanje posjećenosti. Kolačići se postavljaju tek ako kliknete "U redu" — uvijek čuvamo i vaš odabir jezika lokalno u pregledniku. Više informacija u',
    'cookie-banner-link': 'Politici privatnosti',
    'cookie-accept': 'U redu',
    'cookie-decline': 'Odbij',
  },
  en: {
    'nav-services': 'Services & Pricing',
    'nav-portfolio': 'Portfolio',
    'nav-reviews': 'Reviews',
    'nav-about': 'About',
    'nav-faq': 'FAQ',
    'nav-blog': 'Blog',
    'nav-contact': 'Contact',
    'nav-cta': 'Get a Quote',
    'mnav-tagline': 'Web design studio',
    'footer-rights': 'All rights reserved.',
    'footer-privacy': 'Privacy Policy',
    'hero-badge': 'Premium Web Design Studio',
    'hero-h1-pre': 'Websites that',
    'hero-h1-highlight': 'work for you',
    'hero-h1-post': '— not just exist',
    'hero-sub':
      'Modern, fast websites that generate enquiries and show your business the way it truly looks. From the first idea to launch.',
    'hero-btn-portfolio': 'View our work',
    'hero-scroll': 'Scroll to explore',
    'stat-clients': 'Satisfied clients',
    'stat-time': 'Average time to first draft',
    'stat-price': 'Starting price — no hidden costs',
    'hero-price-badge': 'Starting price',

    'services-title': 'Services &amp; Pricing',
    'services-sub':
      'Web design and graphic design — all in one place. Calculate your price instantly or pick a design service.',
    'tab-web': 'Web Calculator',
    'tab-webdesign': 'Web Design',
    'tab-grafika': 'Graphic Design',
    'calc-pick': 'Choose your base',
    'addon-gallery-name': 'Photo gallery',
    'addon-gallery-note': 'Lightbox, categories, mobile-optimised',
    'addon-contact-name': 'Contact form',
    'addon-contact-note': 'With email delivery and spam protection',
    'addon-maps-name': 'Google Maps embed',
    'addon-maps-note': 'Interactive map with your location',
    'addon-logo-name': 'Logo design',
    'addon-logo-note': 'Professional logo included with the site',
    'addon-copy-name': 'Copywriting',
    'addon-copy-note': 'Professional text written for you',
    'addon-seo-name': 'Advanced SEO',
    'addon-seo-note': 'Structured data, sitemap, robots.txt, keyword-optimised meta tags',
    'addon-lang-name': 'Multilingual support',
    'addon-lang-note': 'BA / EN or any other language pair',
    'addon-calc-name': 'Price calculator',
    'addon-calc-note': 'Interactive configurator like this one',
    'addon-admin-name': 'Admin panel',
    'addon-admin-note': 'Edit text and images without touching code',
    'addon-db-name': 'Database',
    'addon-db-note': 'Products, services, posts — fully manageable',
    'addon-maint-name': 'Maintenance plan',
    'addon-maint-note': 'Updates, backups, security checks',
    'addon-maint-price': '/ mo',
    'lang-pick': 'Choose a language...',
    'lang-en': 'English',
    'lang-de': 'German',
    'lang-custom': 'Other (specify in message)',
    'lang-add-btn': 'Add',
    'calc-config-label': 'Your configuration',
    'tier-basic': 'Basic website',
    'tier-pro': 'Professional website',
    'tier-premium': 'Premium website',
    'calc-price-label': 'Estimated price',
    'calc-note': '* Final price confirmed after consultation. No hidden costs.',
    'calc-cta': 'Include this in my inquiry →',
    'calc-reset': 'Reset',
    'calc-show-more': 'Show more',
    'calc-show-less': 'Show less',
    'calc-total-label': 'Total',
    'calc-language-total-label': 'Languages',
    'calc-no-items': 'Select items',
    'bundle-title': 'Combining services? The price drops automatically.',
    'bundle-sub': 'Select Web Design + Graphic Design and the bundle discount is applied in real time.',
    'bundle-discount-label': 'Bundle discount',
    'bundle-th-service': 'Service',
    'bundle-th-standalone': 'Standalone',
    'bundle-th-with': 'With bundle',
    'bundle-th-saved': 'Saved',
    'bundle-th-pay': 'You pay',
    'bundle-total': 'TOTAL',
    'bundle-empty': 'Select at least one calculator item to see the bundle savings.',
    'wd-intro-pre': "Already have a site but it's not delivering? Add features at standard rates. If you decide to",
    'wd-intro-link': 'build a new site',
    'wd-intro-post': 'instead, the same items cost less — up to 20% discount on most add-ons with a new build.',
    'wd-gallery-note': 'Lightbox, categories, mobile-optimised',
    'wd-contact-note': 'With email delivery and spam protection',
    'wd-maps-note': 'Interactive map with your location',
    'wd-copy-note': 'Professional text written for you',
    'wd-seo-note': 'Structured data, sitemap, robots.txt, keyword-optimised meta tags',
    'wd-calc-note': 'Interactive configurator like this one',
    'wd-admin-note': 'Edit text and images without touching code',
    'wd-db-note': 'Products, services, posts — fully manageable',
    'wd-total-note': 'Prices are in EUR and apply to adding features to an existing site.',
    'grafika-intro':
      'Graphic design services available as standalone orders. Click a card to add it to your inquiry.',
    'grafika-selected': 'Selected services',
    'grafika-none-selected': 'Nothing selected yet',
    'grafika-services': 'service(s)',
    'grafika-total': 'Total',
    'grafika-from': 'from',
    'grafika-logo-discount-pre': 'If you buy a',
    'grafika-logo-discount-mid': 'logo together with a website',
    'grafika-logo-discount-post': 'through the calculator, the price drops — see the Web Calculator.',
    'grafika-combo-note': 'Combine graphic design with a website for an extra discount — see the Web Calculator.',

    'portfolio-eyebrow': 'Our work',
    'portfolio-title': 'Portfolio',
    'portfolio-sub': 'Every project is unique. See what we’ve built for our clients.',
    'ba-before': 'Before',
    'ba-after': 'After',
    'pf-case-study': 'Case study',
    'pf-overview': 'Project overview',
    'pf-problem': 'Problem',
    'pf-solution': 'Solution',
    'pf-result': 'Result',
    'pf-techstack': 'Tech stack',
    'pf-expand': 'View details',
    'pf-collapse': 'Hide details',
    'pf-case-study-link': 'View full case study →',
    'reviews-eyebrow': 'What clients say',
    'reviews-title': 'Reviews',
    'reviews-sub': 'Don’t just take our word for it — here’s what those who’ve worked with us have to say.',
    'how-eyebrow': 'The process',
    'how-title': 'How it works',
    'how-sub': 'Four steps from the first conversation to your new website — no complications.',
    'about-eyebrow': 'Who I am',
    'about-title': 'About me',
    'about-p1':
      'Hey! I’m Ermin Avdibašić, a web designer from Kalesija, Canton Tuzla, and founder of EA Studio. I build websites that don’t just look good — they deliver real results: more enquiries, better first impressions, and an online presence your business deserves. I believe every business, regardless of size or industry, needs an online presence that seriously represents its work.',
    'about-p2':
      'I approach every project carefully and with full dedication, working closely with the client from the first idea through to launch. My goal is for your website to be visually attractive, fast, and tailored to the people who will call you to place an order — not just “look nice”.',
    'about-meta-desc':
      'Ermin Avdibašić, founder of EA Studio — a web designer from Kalesija building websites that deliver real results.',
    'faq-eyebrow': 'Frequently asked questions',
    'faq-title': 'Got questions?',
    'faq-sub': 'Here are answers to the most common questions I get from clients.',
    'faq-blog-cta-pre': 'Need more detail, or want to learn something new?',
    'faq-blog-cta-link': 'Check out the blog',
    'faq-blog-cta-post': 'for practical tips on web design, SEO, and digital presence.',

    'blog-eyebrow': 'Blog',
    'blog-title': 'Tips & insights',
    'blog-sub': 'Short, practical articles about web design, SEO, and digital presence for small businesses.',
    'blog-all-posts': 'All articles',
    'blog-back': 'Back to blog',
    'blog-read-more': 'Read more',
    'blog-min-read': 'min read',
    'blog-not-found-title': 'Article not found',
    'blog-not-found-sub': 'The article you’re looking for doesn’t exist or has moved.',
    'blog-not-found-cta': 'Back to blog',
    'blog-breadcrumb-home': 'Home',

    // Phase 5.5 — Inquiry modal (Formspree).
    'inquiry-modal-title': 'Send an inquiry',
    'inquiry-modal-sub': 'Your calculator selection is already included below — just leave your contact details and send.',
    'inquiry-modal-close': 'Close',
    'form-calc-label': 'Your configuration from the calculator',
    'form-fname': 'First name',
    'form-fname-ph': 'Your first name',
    'form-lname': 'Last name',
    'form-lname-ph': 'Your last name',
    'form-email': 'Email address',
    'form-email-ph': 'you@email.com',
    'form-msg': 'Message (optional)',
    'form-msg-ph': 'Any extra notes or questions...',
    'form-submit': 'Send enquiry →',
    'form-submitting': 'Sending...',
    'form-success-title': 'Thank you! Enquiry received.',
    'form-success-sub': "I'll get back to you within 24 hours. If you need a quick reply, feel free to contact me directly on WhatsApp.",
    'form-success-wa': 'Message on WhatsApp',
    'form-error': 'Something went wrong sending your message. Please try again or contact us directly on WhatsApp.',

    // Phase 6 — Kontakt page chrome. The 15-question form itself stays
    // BS-only (see lib/questionnaire.ts) since it's only ever sent as a
    // direct link to an actual BS-speaking client, same as the old
    // upitnik.html — but the page heading/intro is translated as usual.
    'kontakt-eyebrow': "Let's work together",
    'kontakt-title': 'Contact',
    'kontakt-sub':
      "Message directly on WhatsApp or Viber, or fill in the short questionnaire below so I have everything I need to start on your website right away.",
    'kontakt-form-title': 'Or message us directly',
    'kontakt-form-sub': 'A shorter option — just name, email, and message.',

    // Phase 7 — 404 page (no real-site equivalent, new page).
    '404-eyebrow': '404 error',
    '404-title': 'This page drifted off into space',
    '404-sub': 'A UFO probably took it. Double-check the address, or head back somewhere things make sense.',
    '404-cta-home': 'Back to home',
    '404-cta-contact': 'Get in touch',

    // Phase 7 — accessibility (skip link) + Privacy Policy page.
    'skip-to-content': 'Skip to content',
    'privacy-eyebrow': 'Legal',
    'privacy-title': 'Privacy Policy',
    'privacy-intro':
      'This policy explains what data EA Studio collects through this website, how it is used, and who it is never shared with.',
    'privacy-s1-title': '1. Who collects the data',
    'privacy-s1-body':
      'Data is collected by Ermin Avdibašić, owner of EA Studio, as a sole proprietor providing website design and graphic design services in Kalesija, Canton Tuzla, Bosnia and Herzegovina. For any questions about this policy, you can reach me using the contact details on the Contact page.',
    'privacy-s2-title': '2. What data is collected',
    'privacy-s2-body':
      'When you fill in the services calculator inquiry, the questionnaire on the Contact page, or the short contact form, the data you enter is collected: name, email address, and message content, plus your calculator selection where relevant. The site does not collect any data automatically apart from your language preference (see section 4).',
    'privacy-s3-title': '3. How the data is used',
    'privacy-s3-body':
      'Form data is sent directly to my email via the Formspree service, solely to respond to your inquiry and prepare a quote if needed. Data is never used for marketing purposes and is never sold or rented to third parties.',
    'privacy-s4-title': '4. Cookies, local storage and Google Analytics',
    'privacy-s4-body':
      'The site stores your selected language (Bosnian/English) locally in your browser via localStorage — this value never leaves your device. The site also uses Google Analytics to understand visitor traffic, but only if you accept the cookie banner shown on your first visit. Google Analytics cookies are not set until you click "Got it" — if you click "Decline", Analytics never loads. You can change your choice at any time by clearing this site\'s cookies in your browser settings.',
    'privacy-s5-title': '5. Third parties',
    'privacy-s5-body':
      'Form submissions are processed by Formspree (formspree.io), an independent form-handling service — their own privacy policy applies to data transmitted through their infrastructure. The site is hosted on Netlify, which may log standard technical server data (e.g. IP address) for hosting operation and security.',
    'privacy-s6-title': '6. Data security',
    'privacy-s6-body':
      "This site has no database or server of its own storing your data — every form submits directly to Formspree over an encrypted (HTTPS) connection, with no intermediate store that EA Studio holds or could lose. All forms also include basic protection against automated spam.",
    'privacy-s7-title': '7. Your rights',
    'privacy-s7-body':
      'You can request access to, correction of, or deletion of any data you have sent me through a form on this site at any time — just get in touch via the email, WhatsApp, or Viber details on the Contact page.',
    'privacy-updated': 'Last updated: August 2026.',

    // Graphic design & branding — dedicated SEO landing page.
    'gd-eyebrow': 'Graphic Design',
    'gd-title': 'Graphic Design & Branding',
    'gd-sub':
      'Logos, business cards, print materials and brand identity for businesses in Bosnia and Herzegovina — from first sketch to a finished design ready for print or web.',
    'gd-intro-title': 'A visual identity people remember',
    'gd-intro-body':
      "Whether you're launching a new business or refreshing an existing identity, good design is often the first thing a client notices. I design logos, business cards, posters, ads, and everything else your brand needs to look professional — on screen and in print.",
    'gd-services-title': 'Graphic design services',
    'gd-services-sub': 'Every service can be ordered on its own, or combined with a new website for an extra discount.',
    'gd-proof-title': 'Real work, not stock photos',
    'gd-proof-sub': "Logos, business cards and ads actually in use by our clients right now.",
    'gd-proof-tehnostan-logo': 'Logo for Tehnostan — a stonemasonry business in Kalesija',
    'gd-proof-tehnostan-card': 'Business card for Tehnostan',
    'gd-proof-tehnostan-ad': 'Roadside ad for Tehnostan',
    'gd-proof-mmmetal-logo': 'Logo for MM Metal — a metalworking business',
    'gd-combo-title': 'Combine with a website for a discount',
    'gd-combo-body':
      'If you order a logo together with a new website build, the logo price drops — see the Web Calculator for the exact discounted price.',
    'gd-combo-cta': 'Open the calculator',
    'gd-cta-title': 'Ready for a new visual identity?',
    'gd-cta-sub': "Send an inquiry and tell me what you need — I'll get back to you within 24 hours.",
    'gd-cta-btn': 'Get a Quote',

    // Print materials — dedicated SEO landing page, second of 4. Reuses
    // GraphicDesignPanel filtered to print-specific items so it doesn't
    // duplicate the full grid already shown on /graficki-dizajn.
    'pm-eyebrow': 'Print Materials',
    'pm-title': 'Print Materials & Print-Ready Design',
    'pm-sub':
      'Business cards, posters, roadside ads, menus and packaging — design prepared for print, not just for screen.',
    'pm-intro-title': 'Design that looks just as good in hand as on screen',
    'pm-intro-body':
      'Print has different rules than web design — CMYK colours, print resolution, bleed margins. I prepare every file so it is ready to hand straight to the printer, with no surprises or last-minute fixes.',
    'pm-services-title': 'Print design services',
    'pm-services-sub': 'Every service can be ordered on its own, or combined with a new website for an extra discount.',
    'pm-proof-title': 'Materials that are actually printed',
    'pm-proof-sub': 'Roadside ads and business cards our clients actually use out in the field.',
    'pm-proof-tehnostan-ad': 'Roadside ad for Tehnostan — large-format print',
    'pm-proof-tehnostan-ad2': 'A second roadside ad for Tehnostan',
    'pm-proof-tehnostan-card': 'Business card for Tehnostan — double-sided print',
    'pm-combo-title': 'Combine with a website for a discount',
    'pm-combo-body':
      'Print materials can be ordered alongside a new website build at a better price — see the Web Calculator.',
    'pm-combo-cta': 'Open the calculator',
    'pm-cta-title': 'Need something ready for print?',
    'pm-cta-sub': "Send an inquiry with the details — I'll get back to you within 24 hours.",
    'pm-cta-btn': 'Get a Quote',

    // Web design coverage — dedicated SEO landing page, third of 4. Unlike
    // the BS version (which lists BiH cities), the English version drops
    // the city grid entirely and leads with remote/worldwide delivery,
    // since English-language search traffic isn't geographically limited
    // to Bosnia and Herzegovina the way the BS version's audience is.
    'wc-eyebrow': 'Web Design',
    'wc-title': 'Web Design Services, Wherever You Are',
    'wc-sub':
      'Every project is handled remotely — calls, planning, build and launch — so location is never a limiting factor, in Bosnia and Herzegovina or anywhere else.',
    'wc-intro-title': 'Fully remote, from anywhere in the world',
    'wc-intro-body':
      'I work entirely online — video calls, email, WhatsApp — so the process is identical whether you are around the corner or on another continent. No in-person meetings needed, ever.',
    'wc-cities-title': 'Serving clients internationally',
    'wc-cities-sub': "Remote-first from day one — time zones and distance don't change how the project runs.",
    'wc-remote-title': 'What working remotely looks like',
    'wc-remote-body':
      'All planning, file sharing and progress reviews happen over chat and email. The site is hosted online, so you can preview it live throughout the build — from wherever you are.',
    'wc-combo-title': 'Same price, no matter where you are',
    'wc-combo-body': 'Calculator pricing is the same worldwide — there is no surcharge for distance or time zone.',
    'wc-combo-cta': 'Open the calculator',
    'wc-cta-title': 'Ready to start, wherever you are?',
    'wc-cta-sub': "Send an inquiry and tell me a bit about your project — I'll get back to you within 24 hours.",
    'wc-cta-btn': 'Get a Quote',

    // Detailed pricing — dedicated SEO landing page, fourth and last of 4.
    // Static, fully crawlable price list pulling numbers straight from
    // lib/pricing.ts, same as the BS version.
    'cj-eyebrow': 'Pricing',
    'cj-title': 'Detailed Service Pricing',
    'cj-sub':
      'Every price in one place — new website builds, add-ons for an existing site, and graphic design. No hidden costs.',
    'cj-intro-title': 'Why prices are public',
    'cj-intro-body':
      "I believe pricing should be clear before you ever send an inquiry. Below are the exact same prices the interactive calculator on the homepage uses — just laid out as a table here, for a quick overview.",
    'cj-tiers-title': 'Base price for a new website',
    'cj-tiers-sub': 'Price depends on how many add-ons you pick — three thresholds determine the site tier.',
    'cj-tier-basic-range': 'up to {threshold} EUR',
    'cj-tier-pro-range': '{threshold}–{next} EUR',
    'cj-tier-premium-range': '{threshold}+ EUR',
    'cj-tiers-base-note': 'Starting price (bare base, no add-ons): {price} EUR.',
    'cj-addons-title': 'Add-ons for a new website',
    'cj-addons-sub': 'Discounted prices when combined with a new website build. Standard price applies for a standalone order.',
    'cj-th-service': 'Service',
    'cj-th-standalone': 'Standalone',
    'cj-th-bundled': 'With new site',
    'cj-wd-title': 'Add-ons for an existing website',
    'cj-wd-sub': 'Already have a site? These are flat prices for adding features to an existing build.',
    'cj-th-price': 'Price',
    'cj-lang-title': 'Multilingual support',
    'cj-lang-body': 'An extra language (e.g. English or German) added to your website: {price} EUR per language.',
    'cj-grafika-title': 'Graphic design',
    'cj-grafika-sub': 'Standalone graphic design pricing — see the Graphic Design page for real examples of the work.',
    'cj-cta-title': 'See exactly what your project would cost',
    'cj-cta-sub': 'Open the interactive calculator for an exact total based on your selection, or send an inquiry directly.',
    'cj-cta-calc': 'Open the calculator',
    'cj-cta-btn': 'Get a Quote',

    // Terms of Service — new /terms page, same section-array pattern as
    // /privacy.
    'terms-eyebrow': 'Legal',
    'terms-title': 'Terms of Service',
    'terms-intro':
      'These terms describe how EA Studio provides website design, graphic design, and print material services. By using our services or sending an inquiry, you accept these terms.',
    'terms-s1-title': '1. Services',
    'terms-s1-body':
      'EA Studio provides website design, graphic design, branding, and print material services to individual clients, small businesses, and startups. The exact scope of each project is agreed individually before work begins, usually via the questionnaire or a direct conversation.',
    'terms-s2-title': '2. Quotes and pricing',
    'terms-s2-body':
      'Prices shown on the Pricing page and in the calculator are estimates. The final price is confirmed after consultation and depends on the actual project scope. A quote is valid for 14 days from the date it is sent, unless stated otherwise.',
    'terms-s3-title': '3. Payment',
    'terms-s3-body':
      'Larger projects typically require a deposit before work begins, with the remainder due on completion or per an agreed schedule. Exact payment terms are agreed individually and stated in the quote or correspondence before work starts.',
    'terms-s4-title': '4. Timelines',
    'terms-s4-body':
      "An estimated delivery timeline is agreed before the project starts. Timelines depend on project scope and how quickly the client provides materials and feedback — delays in providing content or responses may extend the delivery timeline.",
    'terms-s5-title': '5. Revisions and changes',
    'terms-s5-body':
      'A reasonable number of revisions is included in the price of most services, agreed individually per project. Requests for changes outside the agreed scope (e.g. an entirely new design direction after approval) may incur an additional charge.',
    'terms-s6-title': '6. Ownership and copyright',
    'terms-s6-body':
      "Until the agreed amount is paid in full, all materials produced as part of a project (design, code, graphics) remain the property of EA Studio. Once payment is complete, ownership and usage rights to the final materials transfer to the client, unless otherwise agreed. EA Studio retains the right to showcase completed projects in its portfolio, unless the client explicitly requests otherwise.",
    'terms-s7-title': '7. Client-supplied content',
    'terms-s7-body':
      'The client is responsible for the accuracy, legality, and ownership of any materials they supply (text, photos, logos, data) for use in the project. EA Studio is not liable for third-party copyright infringement in materials supplied by the client.',
    'terms-s8-title': '8. Limitation of liability',
    'terms-s8-body':
      "EA Studio makes reasonable efforts to deliver quality, reliable services, but does not guarantee uninterrupted operation of a website after launch (e.g. due to issues with a hosting provider or third-party services). EA Studio's liability, to the extent permitted by law, is limited to the amount actually paid by the client for the service in question.",
    'terms-s9-title': '9. Cancellation',
    'terms-s9-body':
      'The client may cancel a project at any time before completion, subject to payment for work already carried out up to the point of cancellation. Any deposit paid is non-refundable in that case if work on the project has already begun.',
    'terms-s10-title': '10. Governing law',
    'terms-s10-body':
      'These terms are interpreted in accordance with the laws of Bosnia and Herzegovina. Any dispute will first be addressed through direct communication and good-faith negotiation.',
    'terms-updated': 'Last updated: August 2026.',

    // Cookie/local-storage consent banner.
    'cookie-banner-text':
      'This site uses Google Analytics to understand visitor traffic. Cookies are only set if you click "Got it" — we always store your language preference locally in your browser. More details in the',
    'cookie-banner-link': 'Privacy Policy',
    'cookie-accept': 'Got it',
    'cookie-decline': 'Decline',
  },
} as const satisfies Record<Lang, Record<string, string>>

export type TranslationKey = keyof typeof translations.bs
